import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query'
import hash from 'object-hash'
import { StoreInviteDto } from '@merchx-v2/shared-types'
import axios from 'utils/axios'
import { guardFromErrors, extractErrorInfo } from 'utils/graphqlHelpers'

const DEFAULT_PAGE_SIZE = 30

export type StoreInvitesPage = {
  items: StoreInviteDto[]
  total: number
  currentPage: number
  pageSize: number
}

/// ////////////////////////////////////////////////////////////////////////////////
// GRAPHQL
/// ////////////////////////////////////////////////////////////////////////////////

type QueryResponse = GraphQLResponse<'storeInvitesPage', StoreInvitesPage>

const FETCH_STORE_INVITES_LIST = `
  query fetchStoreInvitesPage ($page: Int, $size: Int, $storeId: Int!) {
    storeInvitesPage(page: $page, size: $size, storeId: $storeId) {
      items {
        email
        status
        createdAt
      }
      total
    }
  }
`

/// ////////////////////////////////////////////////////////////////////////////////
// FUNCTION
/// ////////////////////////////////////////////////////////////////////////////////

type PageParam = {
  page?: number
  size?: number
  storeId: number
}

type FetchStoresInvitesListArgs = {
  queryKey: string | string[]
  pageParam?: PageParam
}

type FetchStoresPage = (args: FetchStoresInvitesListArgs) => Promise<StoreInvitesPage>

const fetchStoreInvitesPage: FetchStoresPage = async ({ pageParam }) => {
  const { page = 1, size = DEFAULT_PAGE_SIZE, storeId } = pageParam || {}
  try {
    const {
      data: { data, errors }
    }: QueryResponse = await axios.post('/graphql', {
      query: FETCH_STORE_INVITES_LIST,
      variables: { page, size, storeId }
    })

    guardFromErrors(errors)

    if (!data) {
      throw new Error('Response body is empty!')
    }

    const { storeInvitesPage } = data
    if (!storeInvitesPage) {
      throw new Error("Can't get store invites page!")
    }

    return {
      items: storeInvitesPage.items,
      total: storeInvitesPage.total,
      currentPage: page,
      pageSize: size
    }
  } catch (error) {
    throw new Error(extractErrorInfo(error))
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK
/// ////////////////////////////////////////////////////////////////////////////////

const queryOptions: UseInfiniteQueryOptions<StoreInvitesPage> = {
  keepPreviousData: true,
  refetchOnMount: true,
  retry: false,

  getPreviousPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0 }) => {
    if (currentPage > 1) {
      return {
        page: currentPage - 1,
        size: pageSize
      }
    }

    return undefined
  },
  getNextPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0, total = 0 }) => {
    if (pageSize * currentPage < total) {
      return {
        page: currentPage + 1,
        size: pageSize
      }
    }

    return undefined
  }
}

type Args = {
  storeId: number
}

export const useStoreInvitesList = (args: Args) => {
  const cacheKey = ['storeInvitesPage', hash(args)]

  return useInfiniteQuery(
    cacheKey,
    (prev) =>
      fetchStoreInvitesPage({
        queryKey: cacheKey,
        pageParam: { ...prev.pageParam, ...args }
      }),
    queryOptions
  )
}
